import { memo, useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import type { PickupListFilters } from "../../hooks/useRentalPickupList";

interface PickupFiltersProps {
  filters: PickupListFilters;
  loading: boolean;
  onApply: (filters: PickupListFilters) => void;
  onClear: () => void;
}

interface FilterDraft {
  expectedOutStartDate: string;
  expectedOutEndDate: string;
  contractNumber: string;
}

function toDraft(filters: PickupListFilters): FilterDraft {
  return {
    expectedOutStartDate: filters.expectedOutStartDate,
    expectedOutEndDate: filters.expectedOutEndDate,
    contractNumber: filters.contractNumbers?.[0] ?? "",
  };
}

function fromDraft(draft: FilterDraft): PickupListFilters {
  return {
    expectedOutStartDate: draft.expectedOutStartDate,
    expectedOutEndDate: draft.expectedOutEndDate,
    contractNumbers: draft.contractNumber.trim()
      ? [draft.contractNumber.trim()]
      : undefined,
  };
}

/**
 * Filtros simplificados da listagem de retirada. Status é fixo
 * (`COMPLETE`) e injetado pelo hook — só ficam expostos os filtros
 * relevantes ao usuário: período de saída prevista, id do aluguel
 * e número do contrato. Mantém um draft interno; commit via `onApply`.
 */
export const PickupFilters = memo(function PickupFilters({
  filters,
  loading,
  onApply,
  onClear,
}: PickupFiltersProps) {
  const [draft, setDraft] = useState<FilterDraft>(() => toDraft(filters));

  useEffect(() => {
    setDraft(toDraft(filters));
  }, [filters]);

  const isInvalid =
    draft.expectedOutStartDate === "" ||
    draft.expectedOutEndDate === "" ||
    draft.expectedOutEndDate < draft.expectedOutStartDate;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="pickup-filter-start">Saída prevista (de)</Label>
          <Input
            id="pickup-filter-start"
            type="date"
            value={draft.expectedOutStartDate}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                expectedOutStartDate: e.target.value,
              }))
            }
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="pickup-filter-end">Saída prevista (até)</Label>
          <Input
            id="pickup-filter-end"
            type="date"
            value={draft.expectedOutEndDate}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                expectedOutEndDate: e.target.value,
              }))
            }
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="pickup-filter-contract">Número do contrato</Label>
          <Input
            id="pickup-filter-contract"
            placeholder="Ex.: 1047"
            value={draft.contractNumber}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, contractNumber: e.target.value }))
            }
            disabled={loading}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={onClear}
          disabled={loading}
          className="gap-2"
        >
          <X className="w-4 h-4" />
          Limpar
        </Button>
        <Button
          onClick={() => onApply(fromDraft(draft))}
          disabled={loading || isInvalid}
          className="gap-2"
        >
          <Search className="w-4 h-4" />
          Aplicar
        </Button>
      </div>
    </div>
  );
});
